/**
 * "Missing from price book" — the office's worklist of casing ticket lines that
 * carry no price-book link, and the one write that adds a book item and links
 * the lines back (api#1040).
 *
 * Shapes are copied from fieldiq-api `src/lib/price-book-missing.ts`
 * (`MissingGroup`, `LinkResult`), camelCase on both read and write — this
 * route does not follow the snake-on-write convention.
 *
 * THE SCREEN COMPUTES NONE OF THE FIGURES. Line, ticket and invoiced counts,
 * the rate range and the dates are the API's, rendered as sent; the grouping
 * itself is the API's (`descriptionKey`), so the client does not re-group or
 * re-count. Which lines a relink takes is the API's call too: the client sends
 * the group's `lineItemIds` and the API skips invoiced lines unless
 * `includeInvoiced` is exactly true.
 *
 * WHICH LINES RELINK. Since api#1044 each group carries its `lines`, and the
 * office ticks them one by one: every not-invoiced line ticked by default,
 * every invoiced line unticked and marked. Against an API without `lines` the
 * choice is made at group level instead — the not-invoiced lines, plus the
 * invoiced ones only on an explicit opt-in (`includeInvoiced`). Either way the
 * per-line outcome comes back in the link response's `linked` / `skipped`.
 */

/** Same four roles the API allows on both routes. Anyone else gets a 403. */
export const PRICE_BOOK_EDIT_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;

export function canEditPriceBook(role: string | null | undefined): boolean {
  return (PRICE_BOOK_EDIT_ROLES as readonly string[]).includes(String(role ?? ''));
}

export interface BookMatch {
  rateSheetItemId: string;
  rateSheetId: string;
  rateSheetName: string;
  serviceName: string;
  unit: string;
  price: number;
}

/**
 * One line of a group (api#1044). `workDate` is `tickets.work_date`, already
 * the tenant's calendar day. `invoiced` lines are linked only when ticked one
 * by one (`confirmInvoicedLineItemIds`).
 */
export interface MissingGroupLine {
  lineItemId: string;
  ticketId: string;
  ticketNumber: string | null;
  workDate: string | null;
  description: string;
  rate: number;
  quantity: number;
  unit: string;
  total: number | null;
  invoiced: boolean;
}

export interface MissingGroup {
  key: string;
  description: string;
  descriptions: string[];
  lineItemType: string;
  unit: string;
  lineCount: number;
  ticketCount: number;
  invoicedLineCount: number;
  relinkableLineCount: number;
  rateMin: number;
  rateMax: number;
  firstWorkDate: string | null;
  lastWorkDate: string | null;
  lineItemIds: string[];
  bookMatches: BookMatch[];
  /**
   * Per-line detail, newest first (api#1044). ABSENT from an API that
   * predates it — and absent is not "no lines": the dialog then falls back to
   * the group-level choice (every not-invoiced line, invoiced on opt-in).
   */
  lines?: MissingGroupLine[];
}

export interface MissingCounts {
  groups: number;
  lines: number;
  relinkableLines: number;
}

export interface MissingItems {
  groups: MissingGroup[];
  counts: MissingCounts | null;
}

/**
 * The GET body's `data`. A `data` that is not the expected object is an
 * unexpected payload, and the screen must not read it as "nothing is missing" —
 * so it throws, and the query's error branch says what happened.
 */
export function readMissingItems(body: any): MissingItems {
  const data = body && typeof body === 'object' && 'data' in body ? body.data : body;
  if (!data || !Array.isArray(data.groups)) {
    throw new Error('The missing-items response did not carry a list of groups.');
  }
  const c = data.counts;
  const counts =
    c && typeof c === 'object'
      ? { groups: Number(c.groups), lines: Number(c.lines), relinkableLines: Number(c.relinkableLines) }
      : null;
  return { groups: data.groups as MissingGroup[], counts };
}

export type SkipReason = 'already_linked' | 'off_book' | 'zero_line' | 'invoiced';

/** Exhaustive over the API's SkipReason. An unknown reason renders as itself, flagged — never a guess. */
export const SKIP_REASON_LABEL: Record<SkipReason, string> = {
  already_linked: 'already linked by someone else since the list loaded',
  off_book: 'marked off book since the list loaded',
  zero_line: 'a $0 line',
  invoiced: 'on an invoiced ticket (not included)',
};

export function skipReasonLabel(reason: string): string {
  return (SKIP_REASON_LABEL as Record<string, string>)[reason] ?? `not linked (${reason})`;
}

export interface LinkRef {
  lineItemId: string;
  ticketId: string;
  ticketNumber: string | null;
}

export interface LinkResult {
  item: { id: string; rateSheetId: string; serviceName: string; unit: string; price: number; created: boolean };
  linked: LinkRef[];
  skipped: Array<LinkRef & { reason: string }>;
  counts: { linked: number; skipped: number };
}

export interface NewItemBody {
  rateSheetId: string;
  serviceName: string;
  unit: string;
  price: number;
}

export type LinkBody =
  | { lineItemIds: string[]; includeInvoiced?: boolean; confirmInvoicedLineItemIds?: string[]; rateSheetItemId: string }
  | { lineItemIds: string[]; includeInvoiced?: boolean; confirmInvoicedLineItemIds?: string[]; item: NewItemBody };

/**
 * The POST body. `includeInvoiced` is sent only when the office ticked it —
 * the API's default (false) is the safe one, and anything but `true` means it.
 */
export function buildLinkBody(
  group: Pick<MissingGroup, 'lineItemIds'>,
  target: { rateSheetItemId: string } | { item: NewItemBody },
  includeInvoiced: boolean
): LinkBody {
  return {
    lineItemIds: group.lineItemIds,
    ...(includeInvoiced ? { includeInvoiced: true } : {}),
    ...target,
  } as LinkBody;
}

/**
 * The POST body when the office ticked lines one by one (api#1044).
 * `lineItemIds` is every ticked line; `confirmInvoicedLineItemIds` is the
 * ticked lines that are invoiced — without it the API skips them. Omitted when
 * none are ticked, so a call that takes no invoiced line reads the same to an
 * API with or without #1044.
 */
export function buildLinkBodyForLines(
  lines: readonly MissingGroupLine[],
  ticked: ReadonlySet<string>,
  target: { rateSheetItemId: string } | { item: NewItemBody }
): LinkBody {
  const chosen = lines.filter((l) => ticked.has(l.lineItemId));
  const invoiced = chosen.filter((l) => l.invoiced).map((l) => l.lineItemId);
  return {
    lineItemIds: chosen.map((l) => l.lineItemId),
    ...(invoiced.length > 0 ? { confirmInvoicedLineItemIds: invoiced } : {}),
    ...target,
  } as LinkBody;
}

/** The default ticks: every line that is not invoiced. */
export function defaultTicked(lines: readonly MissingGroupLine[]): Set<string> {
  return new Set(lines.filter((l) => !l.invoiced).map((l) => l.lineItemId));
}

/** The API caps one call at 500 line ids. */
export const MAX_LINK_LINES = 500;

/** A 409 naming the item that already holds this name and unit on the sheet, or null. */
export function readDuplicateItem(error: any): { message: string; existingItemId: string } | null {
  const body = error?.response?.data;
  if (error?.response?.status !== 409 || typeof body?.existingItemId !== 'string') return null;
  return { message: String(body.error ?? 'That item is already in this price book.'), existingItemId: body.existingItemId };
}

/**
 * The typed rate, or null when it is not a number. NEVER defaulted from the
 * lines' rates: they disagree, which is often why the line is unlinked, and the
 * price is the tenant's to state (the API refuses a missing one).
 */
export function parseRate(input: string): number | null {
  const s = input.trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
